import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/UserAvatar";
import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";

/** Selector de personas del equipo (miembros de un chat). `fijos` no se pueden desmarcar (p. ej. uno mismo al crear). */
export function MiembrosDialog({ open, onOpenChange, titulo, descripcion, inicial = [], fijos = [], textoBoton = "Guardar", onConfirm }) {
  const { usuarios } = useChat();
  const [seleccion, setSeleccion] = useState(new Set(inicial));
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSeleccion(new Set([...inicial, ...fijos]));
      setQ("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtrados = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return usuarios.filter((u) => !needle || u.nombre.toLowerCase().includes(needle) || u.email.includes(needle));
  }, [usuarios, q]);

  const toggle = (email) => {
    if (fijos.includes(email)) return;
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const confirmar = async () => {
    setSaving(true);
    try {
      await onConfirm([...seleccion]);
      onOpenChange(false);
    } catch (_) {
      /* el que llama muestra el error */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {descripcion && <DialogDescription>{descripcion}</DialogDescription>}
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar persona" className="pl-9" />
        </div>
        <ul className="flex-1 overflow-y-auto -mx-2 min-h-[160px]">
          {filtrados.map((u) => {
            const on = seleccion.has(u.email);
            return (
              <li key={u.email}>
                <button
                  type="button"
                  onClick={() => toggle(u.email)}
                  className={cn("w-full flex items-center gap-3 px-2 h-12 rounded-md text-left hover:bg-muted/60", fijos.includes(u.email) && "opacity-70 cursor-default")}
                >
                  <UserAvatar user={u} size={32} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{u.nombre}</span>
                    <span className="block text-xs text-muted-foreground truncate">{u.email}</span>
                  </span>
                  <span className={cn("h-5 w-5 rounded-full border flex items-center justify-center", on ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/30")}>
                    {on && <Check className="w-3 h-3" />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={confirmar} disabled={saving || seleccion.size === 0}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {textoBoton} {seleccion.size > 0 && <span className="ml-1 opacity-70">({seleccion.size})</span>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Avatares apilados de los miembros. */
export function AvataresApilados({ emails = [], max = 4, size = 26 }) {
  const { usuariosByEmail } = useChat();
  const visibles = emails.slice(0, max);
  const resto = emails.length - visibles.length;
  return (
    <div className="flex items-center -space-x-2">
      {visibles.map((e) => (
        <span key={e} className="rounded-full ring-2 ring-background inline-flex" title={usuariosByEmail[e]?.nombre || e}>
          <UserAvatar user={usuariosByEmail[e] || { email: e }} size={size} />
        </span>
      ))}
      {resto > 0 && (
        <span className="rounded-full ring-2 ring-background bg-muted text-[10px] font-medium flex items-center justify-center" style={{ width: size, height: size }}>
          +{resto}
        </span>
      )}
    </div>
  );
}
